// People panel (design side panel "People"): the host's admission queue
// (F-LOB-2) with Admit all, raised hands (F-COM-4), and the roster with
// per-attendee actions on hover — pin (F-CALL-4), private message (F-COM-2),
// and host moderation (F-HOST-1/2/3). Host-wide controls (lock, mute all,
// permission gates) live in their own Host controls panel now. Roles are
// cosmetic here; the server enforces them.

import {
  ChangeEvent,
  FormEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { ChatEvent } from '@brizina/call-engine';

import { api } from '../api';
import { engine } from '../engine';
import { probeRender } from '../perf/renderProbe';
import {
  clearPrivateUnread,
  closePrivateThread,
  openPrivateThread,
  pushToast,
  removeKnock,
  useCallStore,
  useCallStoreShallow,
} from '../store/callStore';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { IconButton } from '../ui/IconButton';
import {
  IconChat,
  IconCheck,
  IconClose,
  IconLock,
  IconMicOff,
  IconMicrophone,
  IconSend,
  IconUserX,
} from '../ui/icons';

export const PeoplePanel = memo(function PeoplePanel({
  slug,
  onClose,
  onOpenChat,
}: {
  slug: string;
  onClose: () => void;
  /** Mobile chat entry — the design's 5-button bar has no chat toggle. */
  onOpenChat?: () => void;
}) {
  probeRender('PeoplePanel');
  const order = useCallStoreShallow((s) => s.order);
  const isHost = useCallStore((s) => s.isHost);
  const knocks = useCallStore((s) => s.knocks);
  // A peer with an open 1:1 thread renders as a compose card IN PLACE at their
  // own roster position (F-COM-2) — the row expands, it doesn't move.
  const privateOpen = useCallStoreShallow((s) => s.openPrivate);

  // Raised hands, oldest first (§9.3: primitives only through useShallow).
  const raisedIds = useCallStoreShallow((s) =>
    s.order
      .filter((id) => s.attendees[id]?.handRaisedAt != null)
      .sort((a, b) => (s.attendees[a]!.handRaisedAt ?? 0) - (s.attendees[b]!.handRaisedAt ?? 0)),
  );

  const decide = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const { knockId, action } = e.currentTarget.dataset;
      if (!knockId) return;
      removeKnock(knockId); // optimistic; WS knock_gone confirms
      void (action === 'admit' ? api.admit(slug, knockId) : api.deny(slug, knockId)).catch(() =>
        pushToast('Decision failed — the request may have expired', 'warning'),
      );
    },
    [slug],
  );

  const admitAll = useCallback(() => {
    for (const k of knocks) {
      removeKnock(k.id);
      void api.admit(slug, k.id).catch(() => {});
    }
  }, [slug, knocks]);

  return (
    <div className="bz-panel bz-people-panel" role="region" aria-label="People">
      <div className="bz-panel-header">
        <span>People</span>
        <div className="bz-panel-header-actions">
          <IconButton shape="flat" label="Close" onClick={onClose}>
            <IconClose size={14} />
          </IconButton>
        </div>
      </div>
      <div className="bz-panel-body">
        {onOpenChat && (
          <button type="button" className="bz-menu-item bz-mobile-only" onClick={onOpenChat}>
            <span className="bz-menu-item-icon">
              <IconChat size={16} />
            </span>
            <span className="bz-menu-item-label">In-call messages</span>
          </button>
        )}
        {isHost && knocks.length > 0 && (
          <div>
            <div className="bz-panel-section-label bz-panel-section-label-warn">
              <span>Waiting to join · {knocks.length}</span>
              {knocks.length > 1 && (
                <button type="button" className="bz-linkbtn" onClick={admitAll}>
                  Admit all
                </button>
              )}
            </div>
            {knocks.map((k) => (
              <div key={k.id} className="bz-knock-row">
                <span className="bz-knock-avatar">
                  <Avatar name={k.name} identity={k.id} size={40} />
                  <span className="bz-knock-dot" />
                </span>
                <span className="bz-knock-name">{k.name}</span>
                <button
                  type="button"
                  className="bz-knock-btn bz-knock-deny"
                  data-knock-id={k.id}
                  data-action="deny"
                  onClick={decide}
                  aria-label="Deny"
                  title="Deny"
                >
                  <IconClose size={17} />
                </button>
                <button
                  type="button"
                  className="bz-knock-btn bz-knock-admit"
                  data-knock-id={k.id}
                  data-action="admit"
                  onClick={decide}
                  aria-label="Admit"
                  title="Admit"
                >
                  <IconCheck size={17} />
                </button>
              </div>
            ))}
          </div>
        )}

        {raisedIds.length > 0 && (
          <div>
            <div className="bz-panel-section-label">
              <span>Raised hands · {raisedIds.length}</span>
            </div>
            {raisedIds.map((id, i) => (
              <HandRow key={id} identity={id} position={i + 1} canLower={isHost} />
            ))}
          </div>
        )}

        <div>
          <div className="bz-panel-section-label">
            <span>In call · {order.length}</span>
          </div>
          <div className="bz-people-list">
            {order.map((id) =>
              privateOpen.includes(id) ? (
                <PrivateCompose key={id} peer={id} />
              ) : (
                <AttendeeRow key={id} slug={slug} identity={id} isHost={isHost} />
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Inline 1:1 private compose (design "Private to X") — pinned at the top of the
// People panel; delivery is destination-scoped so it never hits the room thread.
const PrivateCompose = memo(function PrivateCompose({ peer }: { peer: string }) {
  const name = useCallStore((s) => s.attendees[peer]?.name ?? 'Attendee');
  const isLocal = useCallStore((s) => s.attendees[peer]?.isLocal ?? false);
  const avatarUrl = useCallStore((s) => s.attendees[peer]?.avatarUrl);
  const micOff = useCallStore((s) => s.attendees[peer]?.micEnabled === false);
  const messages = useCallStore((s) => s.privateThreads[peer] ?? EMPTY_THREAD);
  const localIdentity = useCallStore((s) => s.localIdentity);
  const [draft, setDraft] = useState('');
  const firstName = name.split(/\s+/)[0] || name;
  const threadRef = useRef<HTMLDivElement>(null);

  // Reading the thread clears its unread badge; keep it scrolled to newest.
  useEffect(() => {
    clearPrivateUnread(peer);
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [peer, messages.length]);

  const onDraft = useCallback((e: ChangeEvent<HTMLInputElement>) => setDraft(e.target.value), []);
  const close = useCallback(() => closePrivateThread(peer), [peer]);
  const send = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const text = draft.trim();
      if (!text) return;
      setDraft('');
      void engine.sendPrivateChat(peer, text);
    },
    [peer, draft],
  );

  return (
    <div className="bz-dm-card">
      <div className="bz-dm-peer">
        <Avatar name={name} identity={peer} isSelf={isLocal} size={30} photoUrl={avatarUrl} />
        <div className="bz-rowmain">
          <div className="bz-person-name">{name}</div>
          <div className="bz-person-role">Attendee</div>
        </div>
        <span className={micOff ? 'bz-person-mic bz-person-mic-off' : 'bz-person-mic'}>
          {micOff ? <IconMicOff size={14} /> : <IconMicrophone size={14} />}
        </span>
      </div>
      <div className="bz-dm-private">
        <span className="bz-dm-private-label">
          <IconLock size={13} /> Private to {firstName}
        </span>
        <button type="button" className="bz-dm-close" aria-label="Close" onClick={close}>
          <IconClose size={13} />
        </button>
      </div>
      {messages.length > 0 && (
        <div ref={threadRef} className="bz-dm-thread">
          {messages.map((m) => (
            <div
              key={m.id}
              className={m.fromIdentity === localIdentity ? 'bz-dm-msg bz-dm-own' : 'bz-dm-msg'}
            >
              {m.text}
            </div>
          ))}
        </div>
      )}
      <form className="bz-dm-compose" onSubmit={send}>
        <input
          type="text"
          value={draft}
          onChange={onDraft}
          placeholder="Write a private message…"
          maxLength={4000}
          aria-label={`Private message to ${firstName}`}
        />
        <button type="submit" className="bz-dm-send" aria-label="Send">
          <IconSend size={16} />
        </button>
      </form>
    </div>
  );
});

const HandRow = memo(function HandRow({
  identity,
  position,
  canLower,
}: {
  identity: string;
  position: number;
  canLower: boolean;
}) {
  const name = useCallStore((s) => s.attendees[identity]?.name ?? identity);
  const lower = useCallback(() => void engine.lowerHand(identity), [identity]);
  return (
    <div className="bz-knock-row bz-hand-row">
      <span className="bz-hand-badge">✋</span>
      <span className="bz-knock-name">
        {position}. {name}
      </span>
      {canLower && (
        <Button size="sm" onClick={lower}>
          Lower
        </Button>
      )}
    </div>
  );
});

const AttendeeRow = memo(function AttendeeRow({
  slug,
  identity,
  isHost,
}: {
  slug: string;
  identity: string;
  isHost: boolean;
}) {
  const name = useCallStore((s) => s.attendees[identity]?.name ?? identity);
  const isLocal = useCallStore((s) => s.attendees[identity]?.isLocal ?? false);
  const micOff = useCallStore((s) => s.attendees[identity]?.micEnabled === false);
  const avatarUrl = useCallStore((s) => s.attendees[identity]?.avatarUrl);

  const message = useCallback(() => openPrivateThread(identity), [identity]);
  const mute = useCallback(() => {
    void api.muteAttendee(slug, identity).catch(() => pushToast('Could not mute', 'warning'));
  }, [slug, identity]);
  const remove = useCallback(() => {
    void api.removeAttendee(slug, identity).catch(() => pushToast('Could not remove', 'warning'));
  }, [slug, identity]);

  return (
    <div className="bz-person-row">
      <Avatar name={name} identity={identity} isSelf={isLocal} size={32} photoUrl={avatarUrl} />
      <div className="bz-rowmain">
        <div className="bz-person-name">
          {name}
          {isLocal ? ' (you)' : ''}
        </div>
        <div className="bz-person-role">{isLocal ? 'You' : 'Attendee'}</div>
      </div>
      <div className="bz-person-actions">
        {!isLocal && (
          <IconButton shape="square" label="Message" onClick={message}>
            <IconChat size={15} />
          </IconButton>
        )}
        {isHost && !isLocal && (
          <>
            <IconButton shape="square" label="Mute" onClick={mute} disabled={micOff}>
              <IconMicOff size={15} />
            </IconButton>
            <IconButton shape="square" label="Remove" onClick={remove}>
              <IconUserX size={15} />
            </IconButton>
          </>
        )}
      </div>
      <span className={micOff ? 'bz-person-mic bz-person-mic-off' : 'bz-person-mic'}>
        {micOff ? <IconMicOff size={14} /> : <IconMicrophone size={14} />}
      </span>
    </div>
  );
});

const EMPTY_THREAD: ChatEvent[] = [];
